/* Analýza V2+ — položky v cizí měně.
 *
 * PROČ TO EXISTUJE:
 * Klienti drží účty, fondy i dluhy v eurech, dolarech nebo dongách. Dokud se
 * takové položky přepočítávaly „nějakým“ kurzem, ukazovala aplikace číslo,
 * které nikdo neověřil — a poradce ho na schůzce vyslovil jako fakt.
 *
 * PRAVIDLO CELÉHO MODULU:
 * Když kurz chybí, nemá datum, nebo je starší než 30 dní, NEDOSAZUJEME odhad.
 * Vrátíme uplne:false a důvod. Radši prázdné místo než vymyšlená koruna.
 *
 * Čisté funkce, žádný React — testovatelné bez UI.
 *
 * Tvar položky (stejná jména jako v uložených datech):
 *   original_amount  — částka v původní měně
 *   currency         — ISO kód (CZK, EUR, USD, GBP, CHF, PLN, VND)
 *   fx_rate          — kolik Kč za 1 jednotku měny
 *   converted_czk    — přepočet uložený při zápisu (cache, NENÍ zdroj pravdy)
 *   fx_updated_at    — datum kurzu (YYYY-MM-DD nebo ISO čas)
 */

/** Měny, které umíme evidovat. Cokoli mimo seznam je „neznámá měna“. */
export const MENY = ["CZK", "EUR", "USD", "GBP", "CHF", "PLN", "VND"];

/* SYMBOLY MĚN — JEDEN ZDROJ PRO CELOU APPKU.
 * Do 27. 9. 2026 měla tuhle mapu natvrdo `pages/AnalyzaV2Page.js`. Když k ní
 * přibyla boční lišta nástrojů, potřebovala ji taky — a druhá kopie mapy měn
 * je přesně to, co se za půl roku rozejde. Klíče drží krok s `MENY` výš;
 * hlídá to test `lib/analyzaV2/__tests__/menySymboly.test.js`. */
export const SYMBOLY_MEN = { CZK: "Kč", EUR: "€", USD: "$", GBP: "£", CHF: "CHF", PLN: "zł", VND: "₫" };

/** Symbol měny podle kódu. Neznámý kód vrací sám kód — nikdy prázdno,
 *  ať u čísla nezůstane viset holá číslice bez jednotky. */
export function symbolMeny(kod) {
  const k = String(kod || "").trim().toUpperCase();
  return SYMBOLY_MEN[k] || k || "";
}

/* ===================================================================
   DOMÁCÍ (ZÁKLADNÍ) MĚNA

   Určuje se podle ZEMĚ ORGANIZACE poradce, NIKDY podle jazyka aplikace.
   Jazyk a země nejsou totéž: český poradce působící na Slovensku i Slovák,
   který si přepne UI do češtiny, by z jazyka dostali špatnou základnu.

   Proč to vzniklo (změřeno 2026-07): domácí měna byla natvrdo "CZK", takže
   slovenský poradce zadal klientovi 10 000 € a 25 000 € a v souhrnu majetku
   viděl 0 — euro bylo „cizí měna" a bez ručně vyplněného kurzu se nezapočítalo.

   Částka v domácí měně se počítá PŘÍMO, bez kurzu. Kurz se vyžaduje jen
   u skutečně cizích měn.
=================================================================== */

/** Měna podle kódu země organizace. Neznámá země → výchozí (CZK).
 *  ⚠️ KOPIE. Musí souhlasit s `lib/tenantMena.js` (a přes něj s
 *  `backend/routes/auth.py`) — shodu hlídá contexts/__tests__/menaZTenantu.test.js.
 *  Nezůstává tu z lenosti: tenhle modul je čistý (bez Reactu i bez storu), aby
 *  šel testovat bez UI. Když přidáváš zemi, přidej ji na všech třech místech. */
const MENA_ZEME = { CZ: "CZK", SK: "EUR" };

/** Když země není známá. Není to „správná" hodnota, jen nejmenší překvapení. */
export const VYCHOZI_MENA = "CZK";

export function menaZeme(countryCode) {
  const kod = String(countryCode || "").trim().toUpperCase();
  return MENA_ZEME[kod] || VYCHOZI_MENA;
}

/** Je řádek v CIZÍ měně vůči zadané základní měně? (= potřebuje kurz) */
export function jeCiziMena(polozka, base) {
  const mn = normMena(polozka?.currency);
  return !!mn && mn !== zakladniMena(base);
}

/** Normalizace základní měny — nikdy nevrací nesmysl. */
export function zakladniMena(base) {
  return normMena(base) || VYCHOZI_MENA;
}

/** @deprecated Zbylo kvůli starým voláním. Domácí měna NENÍ konstanta —
 *  ber ji z organizace poradce (menaZeme) nebo z analýzy (base_currency). */
export const DOMACI_MENA = "CZK";

/** Za jak dlouho kurz „vyprší“. 30 dní = měsíc, po kterém se u volatilních
 *  měn (VND, CHF) rozdíl už pozná i na klientově souhrnu. */
export const MAX_STARI_KURZU = 30;

/* Důvody, proč se přepočet nepovedl. Konstanty schválně — UI podle nich
 * vybírá hlášku a testy se nechytají na text. */
export const DUVOD_NEZNAMA_MENA = "neznamaMena";
export const DUVOD_CHYBI_CASTKA = "chybiCastka";
export const DUVOD_CHYBI_KURZ = "chybiKurz";
export const DUVOD_CHYBI_DATUM = "chybiDatumKurzu";
export const DUVOD_STARY_KURZ = "staryKurz";
export const DUVOD_BUDOUCI_DATUM = "budouciDatum";

const LOCALE = { cs: "cs-CZ", sk: "sk-SK" };
const locale = (lang) => LOCALE[lang] || LOCALE.cs;

/* Zápis domácí měny. Přepočítáváme do korun, takže „Kč“ i pro slovenské UI —
 * jinak by v jednom řádku svítilo euro jako zdroj i jako výsledek. */
const KC = "Kč";

/** Číslo, nebo null. Prázdný řetězec NENÍ nula — je to chybějící údaj. */
function cislo(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  const n = +v;
  return Number.isFinite(n) ? n : null;
}

/** Kurz musí být kladné konečné číslo. Nula ani záporný kurz kurz není. */
function kurzCislo(v) {
  const n = cislo(v);
  return n != null && n > 0 ? n : null;
}

/** ISO kód měny v podporovaném seznamu, jinak null. */
export function normMena(v) {
  const s = String(v ?? "").trim().toUpperCase();
  return MENY.includes(s) ? s : null;
}

/** Je to měna, kterou umíme? */
export const podporovanaMena = (v) => normMena(v) !== null;

/* Datum (YYYY-MM-DD i ISO čas) na UTC půlnoc. Nesmyslný den (2026-02-31)
 * vrací null — Date by ho tiše překlopil na březen a stáří kurzu by sedělo. */
function denZ(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? "").trim());
  if (!m) return null;
  const [, r, me, d] = m;
  const t = Date.UTC(+r, +me - 1, +d);
  if (!Number.isFinite(t)) return null;
  const dt = new Date(t);
  if (dt.getUTCFullYear() !== +r || dt.getUTCMonth() !== +me - 1 || dt.getUTCDate() !== +d) return null;
  return t;
}

/** Kolik dní je kurzu. Záporné číslo = datum kurzu je v budoucnosti. */
export function stariKurzu(fxUpdatedAt, dnes = new Date()) {
  const kdy = denZ(fxUpdatedAt);
  if (kdy == null) return null;
  const ref = dnes instanceof Date && Number.isFinite(dnes.getTime()) ? dnes : new Date();
  const dnesUtc = denZ(ref.toISOString().slice(0, 10));
  if (dnesUtc == null) return null;
  return Math.round((dnesUtc - kdy) / 86400000);
}

/**
 * Přepočet jedné položky do korun.
 *
 * Vrací { czk, kurzPouzit, stariDny, uplne } + `duvod`, proč to nešlo.
 * czk je null vždy, když uplne === false — aby se nedalo omylem sečíst.
 *
 * CZK položka kurz nepotřebuje: fx_rate = 1, stáří 0.
 * `dnes` je parametr kvůli testovatelnosti (jinak test padá podle kalendáře).
 */
export function prepocet(polozka, dnes = new Date(), base = VYCHOZI_MENA) {
  const zaklMena = zakladniMena(base);
  const mena = normMena(polozka?.currency);
  const castka = cislo(polozka?.original_amount);
  // converted_base je nový název; converted_czk se čte kvůli datům uloženým
  // dřív, kdy základní měna byla vždy CZK. Nově se už nezapisuje.
  const ulozeneCzk = cislo(polozka?.converted_base ?? polozka?.converted_czk);
  const zaklad = {
    mena,
    castka,
    czk: null,
    kurzPouzit: null,
    stariDny: null,
    uplne: false,
    duvod: "",
    ulozeneCzk,
    zakladniMena: zaklMena,
  };

  if (!mena) return { ...zaklad, duvod: DUVOD_NEZNAMA_MENA };
  if (castka == null) return { ...zaklad, duvod: DUVOD_CHYBI_CASTKA };

  // Domácí měna se počítá PŘÍMO — žádný kurz, žádné datum kurzu.
  if (mena === zaklMena) {
    return { ...zaklad, czk: castka, kurzPouzit: 1, stariDny: 0, uplne: true };
  }

  const kurz = kurzCislo(polozka?.fx_rate);
  if (kurz == null) return { ...zaklad, duvod: DUVOD_CHYBI_KURZ };

  const stari = stariKurzu(polozka?.fx_updated_at, dnes);
  // Kurz bez data je stejně nepoužitelný jako žádný kurz — nevíme, z kdy je.
  if (stari == null) return { ...zaklad, kurzPouzit: kurz, duvod: DUVOD_CHYBI_DATUM };
  if (stari < 0) return { ...zaklad, kurzPouzit: kurz, stariDny: stari, duvod: DUVOD_BUDOUCI_DATUM };
  if (stari > MAX_STARI_KURZU) {
    return { ...zaklad, kurzPouzit: kurz, stariDny: stari, duvod: DUVOD_STARY_KURZ };
  }

  return { ...zaklad, czk: castka * kurz, kurzPouzit: kurz, stariDny: stari, uplne: true };
}

/**
 * Sedí uložený přepočet (converted_czk) s tím, co vychází z kurzu?
 * Neshoda znamená, že se od zápisu změnil kurz nebo částka — cache je stará.
 * Tolerance 1 Kč kvůli zaokrouhlení při ukládání.
 */
export function neshodaUlozeneho(polozka, dnes = new Date(), base = VYCHOZI_MENA) {
  const r = prepocet(polozka, dnes, base);
  if (!r.uplne || r.ulozeneCzk == null) return false;
  return Math.abs(r.czk - r.ulozeneCzk) > 1;
}

/**
 * Součet položek v korunách. Do součtu jdou POUZE položky, které se přepočítat
 * daly — zbytek se vrací v `chybejici`, aby ho panel mohl přiznat.
 */
export function soucetCzk(polozky = [], dnes = new Date(), base = VYCHOZI_MENA) {
  const chybejici = [];
  let czk = 0;
  for (const p of polozky || []) {
    const r = prepocet(p, dnes, base);
    if (r.uplne) czk += r.czk;
    else chybejici.push({ polozka: p, duvod: r.duvod, mena: r.mena });
  }
  const celkem = (polozky || []).length;
  return { czk, prepoctenych: celkem - chybejici.length, celkem, chybejici, uplne: chybejici.length === 0 };
}

/* ===================================================================
   FORMÁTOVÁNÍ
=================================================================== */

function formatCisla(n, lang, des = 0) {
  return Number(n).toLocaleString(locale(lang), {
    minimumFractionDigits: des,
    maximumFractionDigits: des,
  });
}

/* Kurz do závorky. U silných měn stačí dvě desetinná místa (23,00),
 * u dongu by z 0,00095 zbyla nula — proto se drobné kurzy nechají delší. */
export function formatKurzu(kurz, lang = "cs") {
  const k = cislo(kurz);
  if (k == null) return null;
  return Number(k).toLocaleString(locale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(k) >= 1 ? 2 : 6,
  });
}

/** Datum kurzu lidsky: 2026-07-30 → „30. 7. 2026“. Nevalidní → null. */
export function datumKurzu(v) {
  const t = denZ(v);
  if (t == null) return null;
  const d = new Date(t);
  return `${d.getUTCDate()}. ${d.getUTCMonth() + 1}. ${d.getUTCFullYear()}`;
}

/**
 * Částka tak, jak ji klient zná: „12 000 USD“, „250 000 Kč“.
 * Chybějící částka nebo neznámá měna → null (UI ukáže pomlčku, ne nulu).
 */
export function puvodniCastka(polozka, lang = "cs") {
  const mena = normMena(polozka?.currency);
  const castka = cislo(polozka?.original_amount);
  if (!mena || castka == null) return null;
  const jednotka = mena === DOMACI_MENA ? KC : mena;
  return `${formatCisla(Math.round(castka), lang)} ${jednotka}`;
}

const NELZE = {
  cs: {
    hlava: "Nelze přepočíst",
    [DUVOD_NEZNAMA_MENA]: "neznámá měna",
    [DUVOD_CHYBI_CASTKA]: "chybí částka",
    [DUVOD_CHYBI_KURZ]: "chybí kurz",
    [DUVOD_CHYBI_DATUM]: "u kurzu chybí datum",
    [DUVOD_STARY_KURZ]: "kurz je starší než 30 dní",
    [DUVOD_BUDOUCI_DATUM]: "datum kurzu je v budoucnosti",
  },
  sk: {
    hlava: "Nedá sa prepočítať",
    [DUVOD_NEZNAMA_MENA]: "neznáma mena",
    [DUVOD_CHYBI_CASTKA]: "chýba suma",
    [DUVOD_CHYBI_KURZ]: "chýba kurz",
    [DUVOD_CHYBI_DATUM]: "pri kurze chýba dátum",
    [DUVOD_STARY_KURZ]: "kurz je starší než 30 dní",
    [DUVOD_BUDOUCI_DATUM]: "dátum kurzu je v budúcnosti",
  },
};

/** Věta „proč to nejde přepočíst“. Pro uplne:true vrací null. */
export function duvodText(duvod, lang = "cs") {
  const t = NELZE[lang] || NELZE.cs;
  if (!duvod || !t[duvod]) return null;
  return `${t.hlava} — ${t[duvod]}`;
}

/**
 * Přepočtená částka i s tím, čím se přepočetla:
 *   „276 000 Kč (kurz 23,00 k 30. 7. 2026)“
 *
 * Když přepočet nejde, vrací se DŮVOD, ne číslo. To je celý smysl modulu:
 * aplikace nesmí tvrdit hodnotu, kterou nezná.
 */
export function prepoctenaCastka(polozka, lang = "cs", dnes = new Date()) {
  const r = prepocet(polozka, dnes);
  if (!r.uplne) return duvodText(r.duvod, lang);
  const castka = `${formatCisla(Math.round(r.czk), lang)} ${KC}`;
  // Koruna na korunu se nepřepočítává — poznámka o kurzu 1,00 by jen mátla.
  if (r.mena === DOMACI_MENA) return castka;
  const kurz = formatKurzu(r.kurzPouzit, lang);
  const kdy = datumKurzu(polozka?.fx_updated_at);
  return `${castka} (kurz ${kurz} k ${kdy})`;
}
